import { useEffect, useState } from "react";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  ModerationRulesetService,
  type ModerationRule,
  type ModerationRuleset,
  type ModerationRulesetAssignment,
} from "@/services/moderation-ruleset.service";

interface ModerationManagementProps {
  catalogueId: string;
  deletedAt?: string;
}

interface ModerationSummary {
  assignment: ModerationRulesetAssignment | null;
  ruleset: ModerationRuleset | null;
  rules: ModerationRule[];
}

export function ModerationManagement({ catalogueId, deletedAt }: ModerationManagementProps) {
  const [summary, setSummary] = useState<ModerationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Assigning from here re-runs the loader rather than patching state locally,
  // so the syncing poll below starts on its own once the assignment lands.
  const [reloadToken, setReloadToken] = useState(0);
  const [rulesets, setRulesets] = useState<ModerationRuleset[] | null>(null);
  const [selectedRulesetId, setSelectedRulesetId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const loadSummary = async (initial: boolean) => {
      if (initial) {
        setLoading(true);
        setError(null);
      }
      try {
        const assignment = await ModerationRulesetService.getCatalogueAssignment(catalogueId);
        if (!assignment) {
          if (active) setSummary({ assignment: null, ruleset: null, rules: [] });
          return;
        }
        const [ruleset, rules] = await Promise.all([
          ModerationRulesetService.getRuleset(assignment.rulesetId),
          ModerationRulesetService.listRules(assignment.rulesetId),
        ]);
        if (active) {
          setSummary({ assignment, ruleset, rules });
          if (assignment.syncStatus === "syncing") {
            refreshTimer = setTimeout(() => void loadSummary(false), 2_000);
          }
        }
      } catch (caught) {
        if (active) setError(caught instanceof Error ? caught.message : "Moderation status could not be loaded.");
      } finally {
        if (active && initial) setLoading(false);
      }
    };

    void loadSummary(true);
    return () => {
      active = false;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [catalogueId, reloadToken]);

  // Only the empty state offers assignment, so the ruleset list is fetched when
  // that state is reached rather than on every visit to an assigned catalogue.
  const needsAssignment = !loading && !error && !summary?.assignment;

  useEffect(() => {
    if (!needsAssignment || deletedAt || rulesets) return;
    let active = true;
    ModerationRulesetService.listRulesets()
      .then((all) => {
        // listRulesets asks for includeArchived=true; an archived ruleset is
        // not a valid assignment target.
        if (active) setRulesets(all.filter((ruleset) => !ruleset.archivedAt));
      })
      .catch((caught) => {
        if (!active) return;
        setRulesets([]);
        setAssignError(
          caught instanceof Error ? caught.message : "Rulesets could not be loaded."
        );
      });
    return () => {
      active = false;
    };
  }, [needsAssignment, deletedAt, rulesets]);

  const assign = async () => {
    if (!selectedRulesetId) return;
    setAssigning(true);
    setAssignError(null);
    try {
      await ModerationRulesetService.setCatalogueAssignment(catalogueId, selectedRulesetId);
      setReloadToken((token) => token + 1);
    } catch (caught) {
      setAssignError(
        caught instanceof Error ? caught.message : "The ruleset could not be assigned."
      );
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-muted-foreground" role="status">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading moderation assignment…
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (!summary?.assignment || !summary.ruleset) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center px-6 py-14 text-center">
          <div className="rounded-full bg-muted p-4">
            <ShieldCheck className="h-7 w-7 text-muted-foreground" />
          </div>
          <h2 className="mt-4 text-lg font-semibold">No ruleset assigned</h2>
          <p className="mt-2 max-w-lg text-sm leading-6 text-muted-foreground">
            This catalogue does not currently run customer-defined moderation rules. Assign one to
            start moderating its products.
          </p>

          {deletedAt ? null : (
            <div className="mt-5 flex w-full max-w-md flex-col gap-2 sm:flex-row">
              <Select
                value={selectedRulesetId}
                onValueChange={setSelectedRulesetId}
                disabled={!rulesets || assigning}
              >
                <SelectTrigger aria-label="Ruleset" className="sm:flex-1">
                  <SelectValue placeholder={rulesets ? "Choose a ruleset" : "Loading rulesets…"} />
                </SelectTrigger>
                <SelectContent>
                  {(rulesets ?? []).map((ruleset) => (
                    <SelectItem key={ruleset.id} value={ruleset.id}>
                      {ruleset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => void assign()} disabled={!selectedRulesetId || assigning}>
                {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {assigning ? "Assigning…" : "Assign"}
              </Button>
            </div>
          )}

          {rulesets && rulesets.length === 0 && !assignError ? (
            <p className="mt-3 text-sm text-muted-foreground">
              No rulesets exist yet. Create one from the moderation rules page.
            </p>
          ) : null}

          {assignError ? (
            <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
              {assignError}
            </p>
          ) : null}

          <Button asChild variant="ghost" className="mt-4">
            <Link to="/catalog/moderation-rules">
              Manage moderation rules <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const activeRules = summary.rules.filter((rule) => rule.active);
  const ready = activeRules.filter((rule) => rule.readiness === "ready").length;
  const preparing = activeRules.filter((rule) => rule.readiness === "preparing").length;
  const attention = activeRules.filter((rule) => rule.readiness === "needs_attention").length;
  const inferredHealth = attention > 0 ? "needs_attention" : preparing > 0 ? "syncing" : "ready";
  const syncStatus = summary.assignment.syncStatus ?? inferredHealth;

  return (
    <div className="space-y-5">
      {deletedAt ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          This catalogue is scheduled for deletion. Its moderation assignment is shown for reference.
        </div>
      ) : null}

      <Card>
        <CardHeader className="border-b">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-lg">{summary.ruleset.name}</CardTitle>
                <SyncBadge status={syncStatus} />
              </div>
              {summary.ruleset.description ? (
                <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                  {summary.ruleset.description}
                </p>
              ) : null}
            </div>
            <Button asChild variant="outline" size="sm" className="shrink-0">
              <Link to={`/catalog/moderation-rules?ruleset=${summary.ruleset.id}`}>
                View ruleset <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryItem label="Active rules" value={activeRules.length} />
            <SummaryItem label="Ready" value={ready} tone="ready" />
            <SummaryItem label="Preparing" value={preparing} tone={preparing ? "preparing" : undefined} />
            <SummaryItem label="Needs attention" value={attention} tone={attention ? "attention" : undefined} />
          </dl>

          <div className="mt-6 rounded-md border">
            <div className="border-b bg-muted/30 px-4 py-3">
              <h3 className="text-sm font-medium">Active rule summary</h3>
            </div>
            {activeRules.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                This ruleset has no active rules.
              </p>
            ) : (
              <div className="divide-y">
                {activeRules.map((rule) => (
                  <div key={rule.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{rule.name}</p>
                      <p className="truncate font-mono text-xs text-muted-foreground">{rule.slug}</p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <Badge variant="outline" className="capitalize">{rule.severity}</Badge>
                      <Badge variant="outline" className={readinessClass(rule.readiness)}>
                        {rule.readiness === "needs_attention" ? "Needs attention" : rule.readiness === "preparing" ? "Preparing" : "Ready"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Effective {rule.effectiveVersion ? `v${rule.effectiveVersion}` : "—"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          This page is a read-only view of the catalogue’s assignment. Manage rules, versions, and
          catalogue assignments from <Link className="font-medium text-foreground underline underline-offset-4" to="/catalog/moderation-rules">Moderation rules</Link>.
        </p>
      </div>
    </div>
  );
}

function SyncBadge({ status }: { status: "syncing" | "ready" | "needs_attention" }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        status === "ready" && "border-emerald-200 bg-emerald-50 text-emerald-700",
        status === "syncing" && "border-sky-200 bg-sky-50 text-sky-700",
        status === "needs_attention" && "border-red-200 bg-red-50 text-red-700"
      )}
    >
      {status === "ready" ? "Up to date" : status === "syncing" ? "Updating" : "Needs attention"}
    </Badge>
  );
}

function readinessClass(readiness: ModerationRule["readiness"]): string {
  if (readiness === "ready") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (readiness === "preparing") return "border-sky-200 bg-sky-50 text-sky-700";
  return "border-red-200 bg-red-50 text-red-700";
}

function SummaryItem({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "ready" | "preparing" | "attention";
}) {
  return (
    <div className={cn(
      "rounded-md border bg-muted/20 p-3",
      tone === "ready" && "border-emerald-200 bg-emerald-50",
      tone === "preparing" && "border-sky-200 bg-sky-50",
      tone === "attention" && "border-red-200 bg-red-50"
    )}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
